// Profile.tsx
import React, { FC, useRef, useState } from "react";
import ReactDOM from "react-dom";
import User from "assets/icons/User.svg?react";
import ChangePassword from "assets/icons/ChangePassword.svg?react";
import Support from "assets/icons/Support.svg?react";
import Logout from "assets/icons/logoutblack.svg?react";
import useClickOutside from "hooks/useClickOutside";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "redux/store/store";
import { setAutoLogoutEnabled } from "redux/slices/autoLogoutSlice";
import { translations } from "utils/i18n";

interface ProfileDropdownProps {
  username?: string;
  lastname?: string;
  email?: string;
  onLogout: () => void;
}

const Profile: FC<ProfileDropdownProps> = ({
  username = "User",
  onLogout,
  email,
  lastname,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Changed ref type to HTMLButtonElement for the trigger
  const iconRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get auto logout state from Redux
  const autoLogoutEnabled = useSelector(
    (state: RootState) => state.autoLogout.enabled,
  );

  // Handle toggle change
  const handleAutoLogoutToggle = (enabled: boolean) => {
    dispatch(setAutoLogoutEnabled(enabled));
  };

  useClickOutside([dropdownRef, iconRef], () => {
    setIsOpen(false);
  });

  const handleNavigate = (tabPath: string) => {
    navigate(`/settings/${tabPath}`);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      {/* Fix S6848 & S1082: Converted div to button for native accessibility */}
      <button
        type="button"
        ref={iconRef}
        className="w-8 h-8 rounded-full bg-gray-white flex items-center justify-center text-deep-blue font-semibold cursor-pointer border-none focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-deep-blue"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {`${username?.charAt(0) || ""}${lastname?.charAt(0) || ""}`.toUpperCase()}
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 backdrop-blur-sm top-11"
            style={{ zIndex: 99999 }}
          >
            <div
              ref={dropdownRef}
              className="absolute right-6 top-1 w-[300px] rounded-md shadow-md bg-white text-base divide-y divide-elevation-2 p-2"
              style={{ zIndex: 99999 }}
            >
              <div className="px-4 py-3">
                {username && (
                  <p className="text-lg font-semibold text-black">
                    {translations.profile.welcome(username)}
                  </p>
                )}
                <p className="text-lg text-black truncate">{email}</p>
                <div className="mt-1 inline-block text-lg font-semibold text-black bg-[#F3F3F0] rounded-full px-4 h-6">
                  {translations.profile.role}
                </div>
              </div>
              <ul className="flex flex-col list-none m-0 p-0">
                {/* Fix S6847 & S1082: Removed listener from li, added interactive button inside */}
                <li>
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2 hover:bg-elevation-2 cursor-pointer flex items-center gap-2"
                    onClick={() => handleNavigate("account")}
                  >
                    <User />
                    <span>{translations.profile.userAccount}</span>
                  </button>
                </li>

                <li>
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2 hover:bg-elevation-2 cursor-pointer flex items-center gap-2"
                    onClick={() => handleNavigate("account")}
                  >
                    <ChangePassword />
                    <span>{translations.profile.changePassword}</span>
                  </button>
                </li>

                <li className="border-b-[1px] border-elevation-2">
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2 hover:bg-elevation-2 cursor-pointer flex items-center gap-2"
                    onClick={() => handleNavigate("support")}
                  >
                    <Support />
                    <span>{translations.profile.support}</span>
                  </button>
                </li>

                <li className="px-4 py-2 border-b-[1px] border-elevation-2">
                  <div className="flex items-center justify-between gap-2">
                    <span>{translations.profile.autoLogout}</span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-sm font-semibold ${
                          autoLogoutEnabled ? "text-green" : "text-red"
                        }`}
                      >
                        {autoLogoutEnabled
                          ? translations.profile.autoLogoutActive
                          : translations.profile.autoLogoutDisabled}
                      </span>
                      <button
                        onClick={() =>
                          handleAutoLogoutToggle(!autoLogoutEnabled)
                        }
                        className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          autoLogoutEnabled
                            ? "bg-green focus:ring-green-500"
                            : "bg-red focus:ring-red-500"
                        }`}
                        role="switch"
                        aria-checked={autoLogoutEnabled}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            autoLogoutEnabled
                              ? "translate-x-7"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </li>

                {/* Fix S6847 & S1082: Logout button */}
                <li>
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2 hover:bg-elevation-2 text-red-500 cursor-pointer flex items-center gap-2"
                    onClick={() => {
                      onLogout();
                      setIsOpen(false);
                    }}
                  >
                    <Logout />
                    <span>{translations.profile.logout}</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Profile;
